import { useMemo, useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Truck, MapPin, ShieldCheck, Info, Minus, Plus, Home, TicketPercent, CreditCard, Search } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/lib/Seo";
import { api, euros, mediaUrl, apiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";
import { usePrices } from "@/components/home/AppOffers";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const KIND_ICONS = { pickup: MapPin, relay: Truck, home: Home };

export default function Acheter() {
  const { deliveryMethods, settings, pricing } = useSite();
  const { price1, price2, compareAt2, promoLabel } = usePrices();
  const [params] = useSearchParams();
  const [offer, setOffer] = useState(params.get("offre") === "1" ? 1 : 2);
  const [deliveryId, setDeliveryId] = useState("");
  const [pickupId, setPickupId] = useState("");
  const [pickups, setPickups] = useState([]);
  const [postalCode, setPostalCode] = useState("");
  const [relayPoints, setRelayPoints] = useState([]);
  const [relayEnabled, setRelayEnabled] = useState(null);
  const [searchingRelay, setSearchingRelay] = useState(false);
  const [relaySelected, setRelaySelected] = useState(null);
  const [relayManual, setRelayManual] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [paying, setPaying] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", address2: "", zip: "", city: "", country: "FR",
  });

  useEffect(() => {
    api.get("/public/pickup-locations").then((r) => setPickups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("annule") === "1") {
      toast.info("Paiement annulé", { description: "Votre commande n'a pas été débitée. Vous pouvez réessayer quand vous voulez." });
    }
  }, [params]);

  useEffect(() => {
    if (!deliveryId && deliveryMethods.length) {
      setDeliveryId(deliveryMethods.find((m) => m.price > 0)?.id || deliveryMethods[0].id);
    }
  }, [deliveryMethods, deliveryId]);

  const selected = deliveryMethods.find((m) => m.id === deliveryId);
  const kind = selected?.kind || (selected?.price === 0 ? "pickup" : "relay");
  const isPickup = kind === "pickup";
  const isRelay = kind === "relay";
  const isHome = kind === "home";
  const unitPrice = offer === 1 ? price1 : price2;
  const savings = offer === 2 ? compareAt2 - price2 : 0;
  const promoDiscount = appliedPromo?.discount || 0;
  const shippingCost = selected ? Number(selected.price) || 0 : 0;
  const total = useMemo(() => unitPrice - promoDiscount + shippingCost, [unitPrice, promoDiscount, shippingCost]);
  const boxPhoto = settings?.product_photo_url || "/api/uploads/boite-reelle.jpg";
  const available = pricing?.offers_available !== false;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const searchRelay = async () => {
    if (postalCode.trim().length < 4) return;
    setSearchingRelay(true);
    setRelaySelected(null);
    try {
      const { data } = await api.get(`/public/service-points?postal_code=${encodeURIComponent(postalCode.trim())}&country=FR`);
      setRelayEnabled(data.enabled);
      setRelayPoints(data.points || []);
      if (!data.enabled) toast.info("Recherche Sendcloud non configurée", { description: "Indiquez simplement votre commune : le point relais le plus proche vous sera confirmé par email." });
    } catch (e) {
      setRelayEnabled(false);
      toast.error(apiError(e, "Recherche de points relais indisponible"));
    } finally {
      setSearchingRelay(false);
    }
  };

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoError("");
    try {
      const { data } = await api.post("/public/promo/validate", { code: promoInput.trim(), subtotal: unitPrice });
      if (data.valid) {
        setAppliedPromo({ code: data.code, discount: data.discount });
        toast.success(`Code ${data.code} appliqué`, { description: `-${euros(data.discount)}` });
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || "Code invalide");
      }
    } catch {
      setAppliedPromo(null);
      setPromoError("Code invalide");
    }
  };

  const missingRelay = isRelay && !relaySelected && !relayManual.trim();

  const handleCheckout = async () => {
    if (missingRelay) { toast.error("Sélectionnez un point relais ou indiquez votre commune"); return; }
    setPaying(true);
    try {
      const payload = {
        offer,
        delivery_method_id: deliveryId,
        pickup_id: isPickup ? pickupId : "",
        relay_id: isRelay && relaySelected ? String(relaySelected.id) : "",
        relay_name: isRelay ? (relaySelected?.name || relayManual.trim()) : "",
        relay_address: isRelay && relaySelected ? `${relaySelected.street} ${relaySelected.house_number}, ${relaySelected.postal_code} ${relaySelected.city}`.trim() : "",
        relay_zip: isRelay && relaySelected ? String(relaySelected.postal_code || "") : "",
        relay_city: isRelay && relaySelected ? String(relaySelected.city || "") : "",
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
        address: isHome ? form.address : "", address2: isHome ? form.address2 : "",
        zip: isHome ? form.zip : "", city: isHome ? form.city : "", country: form.country || "FR",
        promo_code: appliedPromo?.code || "",
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/public/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Impossible de créer le paiement"));
      setPaying(false);
    }
  };

  return (
    <>
      <Seo title="Acheter le jeu" path="/acheter"
        description="Commandez Douzième Homme — Le Jeu, Édition Nice. 1 boîte ou 2 boîtes, retrait gratuit à Nice, point relais ou livraison à domicile. Paiement sécurisé." />

      <section className="py-12 sm:py-16 lg:py-20" data-testid="acheter-page">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-3">Commander</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx">
              Choisissez votre formule
            </h1>
            {promoLabel && <p className="mt-4 inline-block bg-gold/20 text-onyx text-sm font-bold px-4 py-1.5" data-testid="acheter-promo-label">{promoLabel}</p>}
          </Reveal>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7 space-y-10">
              {/* Étape 1 — formule */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="acheter-offers">
                {[{ n: 1, price: price1, label: "1 boîte", desc: "Pour essayer en famille." },
                  { n: 2, price: price2, label: "2 boîtes", desc: "Une pour vous, une pour offrir.", compare: compareAt2, tag: "Le plus choisi" }].map((o) => (
                  <button key={o.n} type="button" onClick={() => setOffer(o.n)} data-testid={`acheter-offer-${o.n}`}
                    className={`relative text-left p-6 border-2 transition-all ${offer === o.n ? "border-rouge bg-white shadow-brand" : "border-line bg-white hover:border-rouge/40"}`}>
                    {o.tag && <span className="absolute -top-3 left-5 bg-rouge text-white text-[10px] font-bold uppercase tracking-wide px-2.5 py-1">{o.tag}</span>}
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-bold uppercase text-onyx">{o.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">{o.desc}</p>
                      </div>
                      <img src={mediaUrl(boxPhoto)} alt="Boîte Douzième Homme — Édition Nice" className="w-14 h-14 object-cover border border-line shrink-0" />
                    </div>
                    <p className="mt-4 flex items-baseline gap-2">
                      <span className="font-display text-3xl font-extrabold text-onyx">{euros(o.price)}</span>
                      {o.compare && <span className="text-sm text-muted-foreground line-through">{euros(o.compare)}</span>}
                    </p>
                    {o.n === 2 && savings > 0 && <p className="text-xs font-bold text-rouge mt-1">Économisez {euros(savings)}</p>}
                  </button>
                ))}
              </div>

              {/* Étape 2 — mode de remise */}
              <div data-testid="delivery-section">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-onyx mb-4">Mode de remise</h2>
                <div className="space-y-3">
                  {deliveryMethods.map((m) => {
                    const mk = m.kind || (m.price === 0 ? "pickup" : "relay");
                    const Icon = KIND_ICONS[mk] || Truck;
                    return (
                      <label key={m.id} data-testid={`delivery-option-${m.id}`}
                        className={`flex items-start gap-4 p-5 border-2 bg-white cursor-pointer transition-all ${deliveryId === m.id ? "border-rouge" : "border-line hover:border-rouge/40"}`}>
                        <input type="radio" name="delivery" className="mt-1 accent-[#C8102E]" checked={deliveryId === m.id} onChange={() => { setDeliveryId(m.id); setRelaySelected(null); }} data-testid={`delivery-radio-${m.id}`} />
                        <span className="flex-1">
                          <span className="flex items-center gap-2 font-display font-bold text-onyx">
                            <Icon className="w-4 h-4 text-rouge" />
                            {m.name}
                          </span>
                          <span className="block text-sm text-muted-foreground mt-1">{m.description}</span>
                        </span>
                        <span className="font-display font-bold text-onyx shrink-0">
                          {m.price === 0 ? "Gratuit" : euros(m.price)}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {isPickup && pickups.length > 0 && (
                  <div className="mt-4">
                    <Label htmlFor="pickup-select">Point de retrait *</Label>
                    <select id="pickup-select" data-testid="pickup-select" required value={pickupId} onChange={(e) => setPickupId(e.target.value)}
                      className="mt-2 w-full border border-input bg-white px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rouge">
                      <option value="">Choisissez un point de retrait…</option>
                      {pickups.map((p) => (
                        <option key={p.id} value={p.id}>{`${p.name} — ${p.address}`}</option>
                      ))}
                    </select>
                  </div>
                )}

                {isRelay && (
                  <div className="mt-4 bg-white border border-line p-5" data-testid="relay-section">
                    <Label>Rechercher un point relais près de chez vous *</Label>
                    <div className="mt-2 flex gap-2">
                      <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} inputMode="numeric" placeholder="Code postal (ex : 06000)"
                        data-testid="relay-search-input" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchRelay())} />
                      <button type="button" onClick={searchRelay} disabled={searchingRelay || postalCode.trim().length < 4} data-testid="relay-search-button"
                        className="inline-flex items-center gap-2 bg-onyx text-cream px-5 text-sm font-bold uppercase tracking-wide hover:bg-rouge transition-colors disabled:opacity-50 shrink-0">
                        <Search className="w-4 h-4" /> {searchingRelay ? "…" : "Chercher"}
                      </button>
                    </div>
                    {relayEnabled === false && (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-2 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 text-rouge shrink-0 mt-0.5" />
                          La sélection automatique Sendcloud sera activée prochainement. Indiquez votre commune :
                        </p>
                        <Input value={relayManual} onChange={(e) => setRelayManual(e.target.value)} data-testid="relay-manual-input"
                          placeholder="Commune / quartier souhaité (ex : Nice Centre)" />
                      </div>
                    )}
                    {relayPoints.length > 0 && (
                      <div className="mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="relay-points-list">
                        {relayPoints.map((p) => (
                          <button key={p.id} type="button" onClick={() => setRelaySelected(p)} data-testid={`relay-point-${p.id}`}
                            className={`w-full text-left p-3.5 border-2 transition-colors ${relaySelected?.id === p.id ? "border-rouge bg-cream" : "border-line hover:border-rouge/40"}`}>
                            <p className="font-semibold text-sm text-onyx">{p.name}</p>
                            <p className="text-xs text-muted-foreground">{p.street} {p.house_number}, {p.postal_code} {p.city} — {p.carrier}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {relayEnabled && !searchingRelay && postalCode && relayPoints.length === 0 && (
                      <p className="mt-3 text-sm text-muted-foreground" data-testid="relay-empty">Aucun point relais trouvé pour ce code postal.</p>
                    )}
                    {relaySelected && (
                      <p className="mt-3 text-sm font-semibold text-field" data-testid="relay-selected">
                        Point sélectionné : {relaySelected.name}, {relaySelected.postal_code} {relaySelected.city}
                      </p>
                    )}
                  </div>
                )}

                {isHome && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="home-address-section">
                    <div className="sm:col-span-2">
                      <Label htmlFor="buyer-address">Adresse *</Label>
                      <Input id="buyer-address" data-testid="buyer-address" value={form.address} onChange={set("address")} className="mt-1.5 bg-white" placeholder="12 rue de France" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="buyer-address2">Complément d'adresse (optionnel)</Label>
                      <Input id="buyer-address2" data-testid="buyer-address2" value={form.address2} onChange={set("address2")} className="mt-1.5 bg-white" placeholder="Bâtiment, étage, digicode…" />
                    </div>
                    <div>
                      <Label htmlFor="buyer-zip">Code postal *</Label>
                      <Input id="buyer-zip" data-testid="buyer-zip" value={form.zip} onChange={set("zip")} className="mt-1.5 bg-white" placeholder="06000" />
                    </div>
                    <div>
                      <Label htmlFor="buyer-city">Ville *</Label>
                      <Input id="buyer-city" data-testid="buyer-city" value={form.city} onChange={set("city")} className="mt-1.5 bg-white" placeholder="Nice" />
                    </div>
                    <div>
                      <Label htmlFor="buyer-country">Pays *</Label>
                      <select id="buyer-country" data-testid="buyer-country" value={form.country} onChange={set("country")}
                        className="mt-1.5 w-full border border-input bg-white px-3 py-2 text-sm">
                        <option value="FR">France</option>
                        <option value="BE">Belgique</option>
                        <option value="CH">Suisse</option>
                        <option value="LU">Luxembourg</option>
                        <option value="MC">Monaco</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Étape 3 — coordonnées */}
              <div data-testid="buyer-form">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-onyx mb-4">Vos coordonnées</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="buyer-first-name">Prénom *</Label>
                    <Input id="buyer-first-name" data-testid="buyer-first-name" required value={form.first_name} onChange={set("first_name")} className="mt-1.5 bg-white" placeholder="Jean" />
                  </div>
                  <div>
                    <Label htmlFor="buyer-last-name">Nom *</Label>
                    <Input id="buyer-last-name" data-testid="buyer-last-name" required value={form.last_name} onChange={set("last_name")} className="mt-1.5 bg-white" placeholder="Dupont" />
                  </div>
                  <div>
                    <Label htmlFor="buyer-email">Email *</Label>
                    <Input id="buyer-email" type="email" data-testid="buyer-email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-white" placeholder="jean@exemple.fr" />
                  </div>
                  <div>
                    <Label htmlFor="buyer-phone">Téléphone</Label>
                    <Input id="buyer-phone" data-testid="buyer-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-white" placeholder="06 00 00 00 00" />
                  </div>
                </div>
              </div>

              {/* Étape 4 — code promo */}
              <div data-testid="promo-section">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-onyx mb-4">Code promo</h2>
                <div className="flex gap-2 max-w-md">
                  <Input value={promoInput} onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }} data-testid="promo-input"
                    placeholder="VOTRE CODE" className="font-mono uppercase bg-white" />
                  <button type="button" onClick={applyPromo} data-testid="promo-apply"
                    className="inline-flex items-center gap-2 border-2 border-onyx px-5 text-sm font-bold uppercase tracking-wide hover:bg-onyx hover:text-cream transition-colors shrink-0">
                    <TicketPercent className="w-4 h-4" /> Appliquer
                  </button>
                </div>
                {promoError && <p className="mt-2 text-sm font-semibold text-destructive" data-testid="promo-error">{promoError}</p>}
                {appliedPromo && <p className="mt-2 text-sm font-semibold text-field" data-testid="promo-applied">Code {appliedPromo.code} appliqué : -{euros(appliedPromo.discount)}</p>}
              </div>
            </div>

            {/* Récapitulatif + paiement */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 bg-onyx text-cream p-7 sm:p-9" data-testid="order-summary">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-6">Récapitulatif</h2>
                <div className="flex items-center gap-4 pb-6 border-b border-cream/15">
                  <img src={mediaUrl(boxPhoto)} alt="Boîte Douzième Homme — Édition Nice" className="w-16 h-16 object-cover" />
                  <div className="flex-1">
                    <p className="font-display font-bold text-sm">Douzième Homme — Le Jeu</p>
                    <p className="text-xs text-cream/55">Édition Nice · {euros(offer === 1 ? price1 : price2 / 2)} / boîte</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" aria-label="Choisir 1 boîte" data-testid="summary-minus" onClick={() => setOffer(1)} disabled={offer === 1}
                      className="w-8 h-8 border border-cream/25 flex items-center justify-center disabled:opacity-30 hover:bg-cream/10 transition-colors">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-display font-bold w-6 text-center" data-testid="summary-quantity">{offer}</span>
                    <button type="button" aria-label="Choisir 2 boîtes" data-testid="summary-plus" onClick={() => setOffer(2)} disabled={offer === 2}
                      className="w-8 h-8 border border-cream/25 flex items-center justify-center disabled:opacity-30 hover:bg-cream/10 transition-colors">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <dl className="py-6 space-y-3 text-sm border-b border-cream/15">
                  <div className="flex justify-between"><dt className="text-cream/60">Sous-total ({offer} boîte{offer > 1 ? "s" : ""})</dt><dd className="font-semibold" data-testid="summary-subtotal">{euros(offer === 2 && savings > 0 ? compareAt2 : unitPrice)}</dd></div>
                  {savings > 0 && <div className="flex justify-between text-gold"><dt>Remise 2 boîtes</dt><dd className="font-semibold" data-testid="summary-savings">-{euros(savings)}</dd></div>}
                  {promoDiscount > 0 && <div className="flex justify-between text-gold"><dt>Code {appliedPromo.code}</dt><dd className="font-semibold" data-testid="summary-promo-discount">-{euros(promoDiscount)}</dd></div>}
                  <div className="flex justify-between"><dt className="text-cream/60">Livraison{selected ? ` — ${selected.name}` : ""}</dt><dd className="font-semibold" data-testid="summary-shipping">{shippingCost === 0 ? "Gratuit" : euros(shippingCost)}</dd></div>
                </dl>
                <div className="flex justify-between items-baseline py-6">
                  <span className="font-display font-bold uppercase tracking-wide">Total TTC</span>
                  <span className="font-display text-3xl font-extrabold" data-testid="summary-total">{euros(total)}</span>
                </div>
                <button type="button" onClick={handleCheckout}
                  disabled={!available || paying || (isPickup && !pickupId) || missingRelay
                    || !form.first_name.trim() || !form.last_name.trim() || !form.email.trim()
                    || (isHome && (!form.address.trim() || !form.zip.trim() || !form.city.trim()))}
                  data-testid="checkout-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-rouge text-white px-6 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                  <CreditCard className="w-4 h-4" /> {paying ? "Redirection vers le paiement…" : `Payer ${euros(total)}`}
                </button>
                {!available && (
                  <p className="mt-3 text-xs text-gold font-semibold" data-testid="checkout-unavailable">
                    Le jeu est momentanément indisponible à la commande en ligne — retrouvez-le en point de vente.
                  </p>
                )}
                <p className="mt-4 flex items-start gap-2 text-xs text-cream/60" data-testid="checkout-notice">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-gold" />
                  Paiement sécurisé par Stripe. Vos coordonnées ne servent qu'à traiter votre commande.
                  En cas de question : <Link to="/contact" className="underline hover:text-rouge-light">contactez-nous</Link>.
                </p>
                <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-cream/55">
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-rouge-light" /> Paiement sécurisé</span>
                  <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-rouge-light" /> Expédition 48h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
